/**
 * Public shared type API for @stockforge/shared/types.
 * Internal app code should import narrower feature modules when they exist.
 */

// ============================================
// Core Enums
// ============================================

export enum ItemPhysicalCategory {
  LIQUID = 'LIQUID',
  FABRIC = 'FABRIC',
  DISCRETE = 'DISCRETE',
}

/** @deprecated Use ItemPhysicalCategory. */
export const MaterialCategory = ItemPhysicalCategory;
/** @deprecated Use ItemPhysicalCategory. */
export type MaterialCategory = ItemPhysicalCategory;

export enum ThresholdType {
  PERCENTAGE = 'PERCENTAGE',
  ABSOLUTE = 'ABSOLUTE',
}

export enum SaleDepletionPolicy {
  STOCK = 'STOCK',
  COMPONENTS = 'COMPONENTS',
  NONE = 'NONE',
}

export enum ReplenishmentPolicy {
  AUTO = 'AUTO',
  PURCHASE = 'PURCHASE',
  PRODUCTION = 'PRODUCTION',
  NONE = 'NONE',
}

export enum IdentificationMethod {
  BARCODE = 'BARCODE',
  IMAGE = 'IMAGE',
  MANUAL = 'MANUAL',
}

export enum POStatus {
  DRAFT = 'DRAFT',
  PENDING_APPROVAL = 'PENDING_APPROVAL',
  SENT = 'SENT',
  CONFIRMED = 'CONFIRMED',
  RECEIVED = 'RECEIVED',
  CANCELLED = 'CANCELLED',
}

export enum UserRole {
  ADMIN = 'ADMIN',
  MANAGER = 'MANAGER',
  OPERATOR = 'OPERATOR',
  VIEWER = 'VIEWER',
}

export enum RecipeComponentType {
  MATERIAL = 'MATERIAL',
  RECIPE = 'RECIPE',
}

export enum RecipeConsumptionMode {
  STOCKED_SUBASSEMBLY = 'STOCKED_SUBASSEMBLY',
  PHANTOM = 'PHANTOM',
}

// ============================================
// Base Entity Interface
// ============================================

export interface BaseEntity {
  id: string;
  createdAt: string;
  updatedAt?: string;
}

// ============================================
// Vendor Model
// ============================================

export interface Vendor extends BaseEntity {
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  contactPerson?: string | null;
  notes?: string | null;
}

export interface VendorWithRelations extends Vendor {
  items?: Item[];
  /** @deprecated Use items. */
  materials?: Material[];
  purchaseOrders?: PurchaseOrder[];
}

export interface VendorCreateInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  address?: string | null;
  contactPerson?: string | null;
  notes?: string | null;
}

export interface VendorUpdateInput extends Partial<VendorCreateInput> {}

// ============================================
// Item Model
// ============================================

export interface Item extends BaseEntity {
  name: string;
  sku: string;
  /** false once delete kept the item for its history; hidden from lists and pickers. */
  isActive?: boolean;
  description?: string | null;
  colorCode?: string | null;
  size?: string | null;
  baseProductCode?: string | null;
  productLine?: string | null;
  brand?: string | null;
  manufacturerPartNumber?: string | null;
  supplierSku?: string | null;
  isSellable: boolean;
  isPurchasable: boolean;
  saleDepletionPolicy: SaleDepletionPolicy;
  replenishmentPolicy: ReplenishmentPolicy;
  category: ItemPhysicalCategory;
  currentQuantity: number;
  unit: string;

  // For LIQUID materials
  density?: number | null;
  densityUnit?: string | null;

  // For FABRIC materials
  width?: number | null;
  widthUnit?: string | null;
  yardsPerRoll?: number | null;

  // Thresholds
  warningThreshold: number;
  criticalThreshold: number;
  thresholdType: ThresholdType;
  lowStockEmailAlertsEnabled: boolean;
  /** Computed: highest on-hand qty from recent inventory history. Not stored. */
  thresholdReferenceQuantity?: number;

  // Freight / pallet
  unitsPerPallet?: number | null;

  // Vendor info
  vendorId?: string | null;
  leadTimeDays: number;
  unitCost: number;

  // Channel identifiers
  upc?: string | null;
  ean?: string | null;
  asin?: string | null;

  // QuickBooks link (set by sync or push)
  qbItemId?: string | null;

  // Provenance: how the material entered StockForge. Server-set; rows created
  // before provenance tracking report MANUAL.
  createdSource?: ItemCreatedSource;
}

export type ItemCreatedSource = 'MANUAL' | 'IMPORT' | 'QBO_SYNC' | 'BARCODE';

export interface ItemWithRelations extends Item {
  vendor?: Vendor | null;
  recipeComponents?: RecipeComponent[];
  poLineItems?: POLineItem[];
  inventoryLogs?: InventoryLog[];
}

export interface ItemCreateInput {
  name: string;
  sku: string;
  description?: string | null;
  colorCode?: string | null;
  size?: string | null;
  baseProductCode?: string | null;
  productLine?: string | null;
  brand?: string | null;
  manufacturerPartNumber?: string | null;
  supplierSku?: string | null;
  isSellable?: boolean;
  isPurchasable?: boolean;
  saleDepletionPolicy?: SaleDepletionPolicy;
  replenishmentPolicy?: ReplenishmentPolicy;
  category: ItemPhysicalCategory;
  currentQuantity?: number;
  unit: string;
  density?: number | null;
  densityUnit?: string | null;
  width?: number | null;
  widthUnit?: string | null;
  yardsPerRoll?: number | null;
  warningThreshold?: number;
  criticalThreshold?: number;
  thresholdType?: ThresholdType;
  lowStockEmailAlertsEnabled?: boolean;
  vendorId?: string | null;
  leadTimeDays?: number;
  unitCost?: number;
}

export interface ItemUpdateInput extends Partial<ItemCreateInput> {}

/** @deprecated Use Item. */
export type Material = Item;
/** @deprecated Use ItemCreatedSource. */
export type MaterialCreatedSource = ItemCreatedSource;
/** @deprecated Use ItemWithRelations. */
export type MaterialWithRelations = ItemWithRelations;
/** @deprecated Use ItemCreateInput. */
export type MaterialCreateInput = ItemCreateInput;
/** @deprecated Use ItemUpdateInput. */
export type MaterialUpdateInput = ItemUpdateInput;

// ============================================
// Recipe Model
// ============================================

export interface Recipe extends BaseEntity {
  name: string;
  sku: string;
  description?: string | null;
  outputQuantity: number;
  outputUnit: string;
  isActive: boolean;
  consumptionMode?: RecipeConsumptionMode;
  /** Finished-good item that stocked production of this recipe produces. Required unless PHANTOM. */
  outputMaterialId?: string | null;
  outputMaterial?: RecipeOutputMaterial | null;
  isDefaultForOutput?: boolean;
  parentRecipeId?: string | null;
}

export interface RecipeOutputMaterial {
  id: string;
  sku: string;
  name: string;
  unit: string;
  currentQuantity?: number;
  isActive?: boolean;
}

export interface RecipeWithRelations extends Recipe {
  components?: RecipeComponentWithRelations[];
  subAssemblies?: RecipeWithRelations[];
  parentRecipe?: Recipe | null;
  productionBatches?: ProductionBatch[];
  referenceImages?: ReferenceImage[];
}

export interface RecipeCreateInput {
  name: string;
  sku: string;
  description?: string | null;
  outputQuantity?: number;
  outputUnit: string;
  isActive?: boolean;
  consumptionMode?: RecipeConsumptionMode;
  outputMaterialId?: string | null;
  isDefaultForOutput?: boolean;
  parentRecipeId?: string | null;
  components?: RecipeComponentInput[];
}

export interface RecipeUpdateInput extends Partial<Omit<RecipeCreateInput, 'components'>> {
  components?: RecipeComponentInput[];
}

// Multi-level BOM tree types
export interface BomTreeNode {
  recipeId: string;
  recipeName: string;
  recipeSku: string;
  outputQuantity: number;
  outputUnit: string;
  depth: number;
  components: BomTreeComponent[];
  subAssemblies: BomTreeNode[];
  totalCost: number;
  costPerUnit: number;
}

export interface BomTreeComponent {
  materialId: string;
  materialName: string;
  materialSku: string;
  standardQty: number;
  actualQty: number;
  unit: string;
  unitCost: number;
  totalCost: number;
  wastageFactor: number;
}

// ============================================
// Recipe Component Model
// ============================================

export interface RecipeComponentBase {
  id: string;
  recipeId: string;
  standardQty: number;
  unit: string;
  wastageFactor: number;
}

export interface MaterialRecipeComponent extends RecipeComponentBase {
  componentType: RecipeComponentType.MATERIAL;
  materialId: string;
  componentRecipeId?: null;
}

export interface ChildRecipeComponent extends RecipeComponentBase {
  componentType: RecipeComponentType.RECIPE;
  materialId?: null;
  componentRecipeId: string;
}

export type RecipeComponent = MaterialRecipeComponent | ChildRecipeComponent;

export interface RecipeComponentWithMaterial extends MaterialRecipeComponent {
  material: Material;
}

export interface RecipeComponentWithRecipe extends ChildRecipeComponent {
  componentRecipe: Pick<Recipe, 'id' | 'name' | 'sku' | 'outputQuantity' | 'outputUnit' | 'consumptionMode'>;
}

export type RecipeComponentWithRelations = RecipeComponentWithMaterial | RecipeComponentWithRecipe;

export interface MaterialRecipeComponentInput {
  componentType?: RecipeComponentType.MATERIAL;
  materialId: string;
  componentRecipeId?: null;
  standardQty: number;
  unit: string;
  wastageFactor?: number;
}

export interface ChildRecipeComponentInput {
  componentType: RecipeComponentType.RECIPE;
  materialId?: null;
  componentRecipeId: string;
  standardQty: number;
  unit: string;
  wastageFactor?: number;
}

export type RecipeComponentInput = MaterialRecipeComponentInput | ChildRecipeComponentInput;

// ============================================
// Size Variants & Copy-to-Size (Tier 2 #2)
// ============================================

export interface SizeVariant {
  id: string;
  label: string;
  volume: number;
  unit: string;
  sortOrder: number;
}

export interface RecipeSizeFamilyMember {
  mappingId: string;
  channel: string;
  size: string;
  recipeId: string;
  recipeSku: string;
  recipeName: string;
  outputQuantity: number;
  outputUnit: string;
  volumeComponentCount: number;
  hasFormula: boolean;
}

export interface RecipeSizeFamily {
  familyKey: string;
  productLine: string | null;
  color: string | null;
  brand: string | null;
  sizes: string[];
  members: RecipeSizeFamilyMember[];
}

// ============================================
// Production Batch Model
// ============================================

export interface ProductionBatch {
  id: string;
  recipeId: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'ROLLED_BACK';
  quantityProduced: number;
  isSpecialBatch: boolean;
  identificationMethod: IdentificationMethod;
  imageReference?: string | null;
  confidenceScore?: number | null;
  notes?: string | null;
  producedAt: string;
  producedBy?: string | null;
}

export interface ProductionBatchWithRelations extends ProductionBatch {
  recipe?: Recipe;
  componentOverrides?: ComponentOverride[];
}

export interface ProductionBatchCreateInput {
  recipeId: string;
  quantityProduced: number;
  isSpecialBatch?: boolean;
  identificationMethod?: IdentificationMethod;
  imageReference?: string | null;
  confidenceScore?: number | null;
  notes?: string | null;
  producedBy?: string | null;
  componentOverrides?: ComponentOverrideInput[];
}

// ============================================
// Component Override Model
// ============================================

export interface ComponentOverride {
  id: string;
  productionBatchId: string;
  materialId: string;
  overrideQty: number;
  unit: string;
}

export interface ComponentOverrideInput {
  materialId: string;
  overrideQty: number;
  unit: string;
}

// ============================================
// Manufacturing Order Model
// ============================================

export type MOStatus = 'DRAFT' | 'SCHEDULED' | 'IN_PROGRESS' | 'COMPLETED' | 'CLOSED' | 'CANCELLED';

export interface ManufacturingOrder extends BaseEntity {
  orderNumber: string;
  recipeId: string;
  status: MOStatus;
  quantity: number;
  outputUnit: string;
  dueDate?: string | null;
  scheduledStart?: string | null;
  actualStart?: string | null;
  completedAt?: string | null;
  priority: number;
  notes?: string | null;
  assignedTo?: string | null;
  createdBy?: string | null;
  runId?: string | null;
}

export interface ManufacturingOrderWithRelations extends ManufacturingOrder {
  recipe?: RecipeWithRelations;
  materialReservations?: MaterialReservation[];
  laborEntries?: LaborEntry[];
  run?: Pick<WorkOrderRun, 'id' | 'runNumber' | 'name'> | null;
}

export interface WorkOrderRun extends BaseEntity {
  runNumber: string;
  name?: string | null;
  scheduledStart?: string | null;
  dueDate?: string | null;
  notes?: string | null;
  createdBy?: string | null;
}

export interface WorkOrderRunWithOrders extends WorkOrderRun {
  orders: ManufacturingOrderWithRelations[];
}

export interface MaterialReservation {
  id: string;
  manufacturingOrderId: string;
  materialId: string;
  reservedQty: number;
  consumedQty: number;
}

// ============================================
// Labor Tracking Model
// ============================================

export interface LaborEntry {
  id: string;
  manufacturingOrderId?: string | null;
  productionBatchId?: string | null;
  userId: string;
  clockIn: string;
  clockOut?: string | null;
  breakMinutes: number;
  hourlyRate?: number | null;
  laborCost?: number | null;
  notes?: string | null;
}

export interface LaborSummary {
  totalHours: number;
  totalCost: number;
  entriesCount: number;
  avgHoursPerEntry: number;
}

// ============================================
// Approval Workflow Model
// ============================================

export type ApprovalStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';

export interface ApprovalWorkflow {
  id: string;
  name: string;
  entityType: string;
  isActive: boolean;
  thresholdAmount?: number | null;
  steps?: ApprovalStep[];
}

export interface ApprovalStep {
  id: string;
  workflowId: string;
  stepOrder: number;
  approverRole: string;
  approverUserId?: string | null;
  isRequired: boolean;
}

export interface ApprovalRecord {
  id: string;
  workflowId: string;
  entityType: string;
  entityId: string;
  currentStep: number;
  status: ApprovalStatus;
  requestedBy: string;
  decisions?: ApprovalDecision[];
}

export interface ApprovalDecision {
  id: string;
  recordId: string;
  stepOrder: number;
  decidedBy: string;
  decision: ApprovalStatus;
  comments?: string | null;
  decidedAt: string;
}

// ============================================
// Purchase Order Model
// ============================================

export interface PurchaseOrder extends Omit<BaseEntity, 'updatedAt'> {
  vendorId: string;
  status: POStatus;
  totalEstimatedCost: number;
  notes?: string | null;
  approvedAt?: string | null;
  sentAt?: string | null;
  receivedAt?: string | null;
}

export type PurchaseOrderAction = 'send' | 'confirm' | 'receive' | 'cancel';

export interface PurchaseOrderWithRelations extends PurchaseOrder {
  vendor?: Vendor;
  lineItems?: POLineItemWithMaterial[];
  nextActions?: PurchaseOrderAction[];
}

export interface PurchaseOrderCreateInput {
  vendorId: string;
  status?: POStatus;
  notes?: string | null;
  lineItems?: POLineItemInput[];
}

export interface PurchaseOrderUpdateInput extends Partial<Omit<PurchaseOrderCreateInput, 'vendorId' | 'lineItems'>> {
  lineItems?: POLineItemInput[];
}

// ============================================
// PO Line Item Model
// ============================================

export interface POLineItem {
  id: string;
  purchaseOrderId: string;
  materialId: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  lineTotal: number;
}

export interface POLineItemWithMaterial extends POLineItem {
  material: Material;
}

export interface POLineItemInput {
  materialId: string;
  quantity: number;
  unit: string;
  unitPrice?: number;
}

// ============================================
// Reference Image Model (for AI recognition)
// ============================================

export interface ReferenceImage {
  id: string;
  recipeId: string;
  imagePath: string;
  angle?: string | null;
  features?: string | null;
  createdAt: string;
}

// ============================================
// Inventory Log (Audit Trail)
// ============================================

export interface InventoryLog {
  id: string;
  materialId: string;
  previousQty: number;
  newQty: number;
  changeAmount: number;
  reason: string;
  referenceId?: string | null;
  createdAt: string;
  createdBy?: string | null;
}

export interface InventoryLogWithMaterial extends InventoryLog {
  material: Material;
}

// ============================================
// User & Authentication
// ============================================

export interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  organizationId?: string | null;
  emailVerified?: boolean;
  onboardingCompleted?: boolean;
  onboardingStep?: number;
}

export interface UserResponse extends User {
  emailVerified: boolean;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface OnboardingStatus {
  onboardingCompleted: boolean;
  onboardingStep: number;
}

export interface PasskeyInfo {
  id: string;
  deviceName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface TokenPair {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface AuthResponse {
  message: string;
  user: User;
  tokens: TokenPair;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface AuthState {
  user: User | null;
  accessToken: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

// ============================================
// API Response Types
// ============================================

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface ApiError {
  message: string;
  code: string;
  details?: Record<string, unknown>;
  validationErrors?: Array<{
    field: string;
    message: string;
  }>;
  statusCode: number;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: ApiError;
}

// ============================================
// Dashboard & Analytics Types
// ============================================

export interface DashboardStats {
  totalMaterials: number;
  lowStockCount: number;
  criticalStockCount: number;
  pendingOrdersCount: number;
  todayProductionCount: number;
  totalInventoryValue: number;
}

export interface MaterialStockStatus {
  material: Material;
  status: 'healthy' | 'warning' | 'critical';
  daysUntilDepletion?: number;
}

export interface ProductionSummary {
  date: string;
  totalBatches: number;
  totalQuantity: number;
  recipes: {
    recipeId: string;
    recipeName: string;
    batches: number;
    quantity: number;
  }[];
}

export interface DepletionForecast {
  materialId: string;
  materialName: string;
  currentQuantity: number;
  dailyBurnRate: number;
  daysUntilDepletion: number;
  depletionDate: string;
  leadTimeDays: number;
  reorderPoint: number;
  status: 'safe' | 'order_soon' | 'urgent' | 'critical';
}

export interface ActionItem {
  id: string;
  type: 'ai_confirmation' | 'po_approval' | 'blocked_recipe' | 'low_stock' | 'reorder';
  priority: 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  relatedId?: string;
  relatedType?: 'material' | 'recipe' | 'po' | 'production';
  createdAt: string;
  dueDate?: string;
}

// ============================================
// Filter & Sort Types
// ============================================

export interface MaterialFilters {
  category?: MaterialCategory;
  vendorId?: string;
  stockStatus?: 'healthy' | 'warning' | 'critical';
  search?: string;
}

export interface RecipeFilters {
  isActive?: boolean;
  search?: string;
}

export interface PurchaseOrderFilters {
  status?: POStatus;
  vendorId?: string;
  dateFrom?: string;
  dateTo?: string;
}

export type SortDirection = 'asc' | 'desc';

export interface SortConfig<T> {
  field: keyof T;
  direction: SortDirection;
}

// ============================================
// Chart & Report Types
// ============================================

export interface TimeSeriesDataPoint {
  date: string;
  value: number;
  label?: string;
}

export interface InventoryTrendData {
  materialId: string;
  materialName: string;
  data: TimeSeriesDataPoint[];
}

export interface ProductionVolumeData {
  recipeId: string;
  recipeName: string;
  data: TimeSeriesDataPoint[];
}

export interface ConsumptionTrendData {
  materialId: string;
  materialName: string;
  data: TimeSeriesDataPoint[];
}

export interface VendorPerformanceData {
  vendorId: string;
  vendorName: string;
  totalOrders: number;
  onTimeDeliveryRate: number;
  averageLeadTime: number;
  qualityScore: number;
}

export interface WastageAnalysisData {
  materialId: string;
  materialName: string;
  totalUsed: number;
  totalWasted: number;
  wastagePercentage: number;
  period: string;
}

// ============================================
// Kit / Bundle Model
// ============================================

export interface Kit extends BaseEntity {
  name: string;
  sku: string;
  description?: string | null;
  isActive: boolean;
  organizationId: string;
}

export interface KitComponent {
  id: string;
  kitId: string;
  recipeId?: string | null;
  materialId?: string | null;
  quantity: number;
}

export interface KitComponentWithRelations extends KitComponent {
  recipe?: Recipe | null;
  material?: Material | null;
}

export interface KitWithRelations extends Kit {
  components: KitComponentWithRelations[];
}

// ============================================
// Custom Fields Model
// ============================================

export enum CustomFieldType {
  TEXT = 'TEXT',
  NUMBER = 'NUMBER',
  DATE = 'DATE',
  BOOLEAN = 'BOOLEAN',
  SELECT = 'SELECT',
  MULTISELECT = 'MULTISELECT',
  URL = 'URL',
}

export type CustomFieldEntityType = 'MATERIAL' | 'RECIPE' | 'PURCHASE_ORDER' | 'KIT';

export interface CustomFieldDefinition extends BaseEntity {
  name: string;
  fieldKey: string;
  fieldType: CustomFieldType;
  entityType: CustomFieldEntityType;
  isRequired: boolean;
  defaultValue?: string | null;
  options?: string[] | null;
  sortOrder: number;
  organizationId: string;
}

export interface CustomFieldValue {
  id: string;
  definitionId: string;
  entityId: string;
  value: string;
  createdAt: string;
  updatedAt: string;
}

export interface CustomFieldValueWithDefinition extends CustomFieldValue {
  definition: CustomFieldDefinition;
}

export interface KitCreateInput {
  name: string;
  sku: string;
  description?: string;
  components: KitComponentInput[];
}

export interface KitComponentInput {
  recipeId?: string;
  materialId?: string;
  quantity: number;
}

export interface KitUpdateInput {
  name?: string;
  sku?: string;
  description?: string | null;
  isActive?: boolean;
  components?: KitComponentInput[];
}

export interface KitAssemblyResult {
  kitId: string;
  kitName: string;
  quantityAssembled: number;
  deductions: {
    materialId: string;
    materialName: string;
    deducted: number;
    remaining: number;
    unit: string;
  }[];
}

export interface KitFeasibility {
  kitId: string;
  kitName: string;
  isFeasible: boolean;
  components: {
    componentId: string;
    materialId: string;
    materialName: string;
    required: number;
    available: number;
    unit: string;
    isSufficient: boolean;
  }[];
  shortages: {
    materialId: string;
    materialName: string;
    required: number;
    available: number;
    shortage: number;
    unit: string;
  }[];
}

// ============================================
// RMA (Return Merchandise Authorization)
// ============================================

export type RMAStatus = 'REQUESTED' | 'APPROVED' | 'INSPECTING' | 'RESOLVED' | 'CANCELLED';

export type RMAResolution = 'RESTOCK' | 'REPAIR' | 'SCRAP' | 'REPLACE';

export interface RMA extends BaseEntity {
  rmaNumber: string;
  status: RMAStatus;
  customerName: string;
  customerEmail?: string | null;
  reason: string;
  resolution?: RMAResolution | null;
  resolutionNotes?: string | null;
  materialId?: string | null;
  quantity: number;
  unit: string;
  lotId?: string | null;
  serialNumber?: string | null;
  inspectedBy?: string | null;
  inspectedAt?: string | null;
  resolvedAt?: string | null;
  createdBy?: string | null;
}

export interface RMAWithRelations extends RMA {
  material?: Material | null;
}

export interface RMACreateInput {
  customerName: string;
  customerEmail?: string;
  reason: string;
  materialId?: string;
  quantity: number;
  unit: string;
  lotId?: string;
  serialNumber?: string;
}

export interface RMAUpdateInput extends Partial<RMACreateInput> {}

export interface RMAStats {
  byStatus: Record<string, number>;
  byResolution: Record<string, number>;
  total: number;
  resolvedThisMonth: number;
}

// ============================================
// Packing / Container Management
// ============================================

export type PackingStatus = 'OPEN' | 'PACKED' | 'SEALED' | 'SHIPPED';

export type ContainerType = 'BOX' | 'PALLET' | 'CRATE' | 'ENVELOPE' | 'TUBE';

export interface PackingContainer extends BaseEntity {
  containerNumber: string;
  name?: string | null;
  containerType: string;
  status: PackingStatus;
  weight?: number | null;
  weightUnit?: string | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  dimensionUnit?: string | null;
  notes?: string | null;
  shipmentId?: string | null;
  organizationId: string;
  createdBy?: string | null;
}

export interface PackingItem {
  id: string;
  containerId: string;
  materialId?: string | null;
  description?: string | null;
  quantity: number;
  unit: string;
  serialNumber?: string | null;
  lotNumber?: string | null;
  createdAt: string;
}

export interface PackingItemWithMaterial extends PackingItem {
  material?: {
    id: string;
    name: string;
    sku: string;
    unit: string;
  } | null;
}

export interface PackingContainerWithItems extends PackingContainer {
  items: PackingItemWithMaterial[];
}

export interface PackingContainerCreateInput {
  name?: string;
  containerType?: ContainerType;
  weight?: number;
  weightUnit?: string;
  length?: number;
  width?: number;
  height?: number;
  dimensionUnit?: string;
  notes?: string;
  shipmentId?: string;
}

export interface PackingContainerUpdateInput extends Partial<PackingContainerCreateInput> {}

export interface PackingItemCreateInput {
  materialId?: string;
  description?: string;
  quantity: number;
  unit: string;
  serialNumber?: string;
  lotNumber?: string;
}

export interface PackingList {
  containerNumber: string;
  name?: string | null;
  containerType: string;
  status: PackingStatus;
  dimensions: {
    length: number;
    width: number;
    height: number;
    unit: string;
  } | null;
  weight: {
    value: number;
    unit: string;
  } | null;
  shipmentId?: string | null;
  notes?: string | null;
  createdAt: string;
  totalItems: number;
  items: {
    id: string;
    material: { id: string; name: string; sku: string } | null;
    description: string | null;
    quantity: number;
    unit: string;
    serialNumber: string | null;
    lotNumber: string | null;
  }[];
}

export interface PackingStats {
  byStatus: Record<string, number>;
  total: number;
}

// ============================================
// Subcontracting Types
// ============================================

export type SubcontractStatus =
  | 'DRAFT'
  | 'SENT'
  | 'ACKNOWLEDGED'
  | 'IN_PROGRESS'
  | 'QUALITY_CHECK'
  | 'COMPLETED'
  | 'CANCELLED';

export interface SubcontractOrder extends BaseEntity {
  orderNumber: string;
  status: SubcontractStatus;
  vendorId: string;
  manufacturingOrderId?: string | null;
  description: string;
  quantity: number;
  unit: string;
  unitCost?: number | null;
  totalCost?: number | null;
  sentDate?: string | null;
  expectedReturn?: string | null;
  actualReturn?: string | null;
  notes?: string | null;
  organizationId: string;
  createdBy?: string | null;
}

export interface SubcontractItem {
  id: string;
  subcontractOrderId: string;
  materialId: string;
  quantitySent: number;
  quantityReturned?: number | null;
  unit: string;
  notes?: string | null;
  createdAt: string;
}

export interface SubcontractOrderWithRelations extends SubcontractOrder {
  vendor: { id: string; name: string; email?: string | null };
  manufacturingOrder?: { id: string; orderNumber: string } | null;
  items: (SubcontractItem & {
    material: { id: string; name: string; sku: string };
  })[];
}

export interface SubcontractCreateInput {
  vendorId: string;
  manufacturingOrderId?: string;
  description: string;
  quantity: number;
  unit: string;
  unitCost?: number;
  totalCost?: number;
  expectedReturn?: string;
  notes?: string;
}

export interface SubcontractStats {
  byStatus: Record<string, number>;
  totalCost: number;
  activeOrders: number;
  total: number;
}

// ============================================
// Capacity Planning Types
// ============================================

export type ResourceType = 'WORKSTATION' | 'MACHINE' | 'PERSONNEL' | 'TOOL';

export interface Resource {
  id: string;
  name: string;
  type: ResourceType;
  description: string | null;
  isActive: boolean;
  capacityPerHour: number | null;
  hoursPerDay: number;
  daysPerWeek: number;
  costPerHour: number | null;
  location: string | null;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
  schedules?: ResourceSchedule[];
}

export interface ResourceSchedule {
  id: string;
  resourceId: string;
  manufacturingOrderId: string | null;
  startTime: string;
  endTime: string;
  notes: string | null;
  createdAt: string;
  resource?: { id: string; name: string; type: ResourceType };
  manufacturingOrder?: { id: string; orderNumber: string; status?: string } | null;
}

export interface CapacityOverview {
  resourceId: string;
  resourceName: string;
  resourceType: ResourceType;
  totalAvailableHours: number;
  scheduledHours: number;
  utilizationPercent: number;
  costPerHour: number | null;
}

export interface BottleneckReport {
  resourceId: string;
  resourceName: string;
  resourceType: ResourceType;
  totalAvailableHours: number;
  scheduledHours: number;
  utilizationPercent: number;
  costPerHour: number | null;
  severity: 'WARNING' | 'CRITICAL';
}

export interface ResourceAvailability {
  start: string;
  end: string;
  durationHours: number;
}

// ============================================
// Master Production Schedule (MPS) Types
// ============================================

export type MPSStatus = 'DRAFT' | 'PUBLISHED' | 'ACTIVE' | 'COMPLETED' | 'ARCHIVED';

export interface ProductionPlan extends BaseEntity {
  name: string;
  status: MPSStatus;
  startDate: string;
  endDate: string;
  notes?: string | null;
  publishedAt?: string | null;
  publishedBy?: string | null;
  organizationId: string;
  createdBy?: string | null;
  items?: ProductionPlanItem[];
  _count?: { items: number };
}

export interface ProductionPlanItem {
  id: string;
  planId: string;
  recipeId: string;
  recipe?: { id: string; name: string; sku: string; outputUnit: string };
  weekNumber: number;
  year: number;
  plannedQuantity: number;
  unit: string;
  priority: number;
  notes?: string | null;
  manufacturingOrderId?: string | null;
  createdAt: string;
}

export interface DemandForecast {
  id: string;
  recipeId: string;
  recipe?: { id: string; name: string; sku: string; outputUnit: string };
  weekNumber: number;
  year: number;
  forecastQuantity: number;
  actualQuantity?: number | null;
  unit: string;
  source: string;
  organizationId: string;
  createdAt: string;
  updatedAt: string;
}

export interface MaterialRequirement {
  materialId: string;
  materialName: string;
  materialSku: string;
  requiredQuantity: number;
  currentStock: number;
  shortage: number;
  unit: string;
}

export interface MPSStats {
  totalPlans: number;
  byStatus: Record<string, number>;
  activePlanItems: number;
  totalShortages: number;
}

// ============================================
// Scan & Vision Types
// ============================================

export type BarcodeFormat = 'UPC_A' | 'UPC_E' | 'EAN_8' | 'EAN_13' | 'CODE_128' | 'QR_CODE';

export interface BarcodeResult {
  type: string;
  data: string;
  bounds?: {
    origin: { x: number; y: number };
    size: { width: number; height: number };
  };
}

export interface ScanResult {
  barcode: string;
  material?: Material;
  recipe?: Recipe;
  recognized: boolean;
  confidence?: number;
}

export {
  classifyItemStockStatus,
  classifyItemTreatment,
  describeThresholdReference,
  formatItemThreshold,
  isStockedPolicies,
  isWritableItemTreatment,
  ITEM_TREATMENT_HELP,
  ITEM_TREATMENT_LABELS,
  itemTreatmentWhere,
  policiesForItemTreatment,
  STOCKED_POLICY_CASES,
  stockAlertsEnabledForSaleDepletion,
  WRITABLE_ITEM_TREATMENTS,
} from './itemTreatment';
export type {
  ItemStockStatus,
  ItemTreatment,
  ItemTreatmentPolicies,
  WritableItemTreatment,
} from './itemTreatment';
export { parseReorderPointOverrideInput } from './reorderPoint';
export type { ParsedReorderPointOverride } from './reorderPoint';
